/**
 * 路由守卫兜底放行后的轻量错误提示区（WR-346）
 *
 * 背景：初始化接口（/ccp/initLoginUser）客户端超时时，守卫会兜底放行以避免整页白屏，
 * 但此时没有用户信息，页面是"能渲染但内容空"的空壳。这里给一条轻量提示 + 重试按钮，
 * 让用户知道发生了什么、并能一键重载恢复。
 *
 * 为什么用原生 DOM 而不是 .vue 组件：本提示要在 router.beforeEach 里（Vue 根实例尚未
 * 挂载完成、也拿不到组件上下文时）随时插入，不依赖任何父组件；同时避免改 App.vue
 * （本次改动的文件清单不含 App.vue）。
 * [Claude Code]
 */

export const GUARD_ERROR_TIP_ID = 'guardErrorTip'

// 取 i18n 文案；i18n 是挂在 window 上的全局实例（同 router.js 的用法），取不到就回落成 key
const t = key => {
  try {
    return typeof i18n !== 'undefined' && i18n && typeof i18n.t === 'function' ? i18n.t(key) : key
  } catch (e) {
    return key
  }
}

/**
 * 展示错误提示区（重复调用只保留一个）
 * @param {Object} options
 * @param {String} options.message 自定义提示文案，默认取 lang.initUserInfoFailedTips
 */
export function showGuardErrorTip (options = {}) {
  if (typeof document === 'undefined' || !document.body) return null
  // 已存在则不重复插入，避免多次导航叠加多条提示；但文案要按最新原因更新（超时 / 无可用页面）
  const exist = document.getElementById(GUARD_ERROR_TIP_ID)
  if (exist) {
    const existText = exist.querySelector('.guard-error-tip__text')
    if (existText) existText.textContent = options.message || t('lang.initUserInfoFailedTips')
    return exist
  }

  const box = document.createElement('div')
  box.id = GUARD_ERROR_TIP_ID
  box.className = 'guard-error-tip'
  // WR-346 MINOR-5：贴**底**而不是贴顶 —— CC 页面底部无固定元素，不遮挡任何东西；
  // 也不需要知道 HeaderCc 的高度（顶栏高度随语言/角色变化，硬编码 top:60px 会错位）；
  // 仍是 fixed 覆盖式，不推开内容、不触发重排。
  // 样式逐属性赋值而不是 setAttribute('style', …)：tests/jest.setup.js:182-186 把所有 div 的
  // setAttribute stub 成了空函数，用 setAttribute 写的样式在单测里既不生效也无法断言 [Claude Code]
  Object.assign(box.style, {
    position: 'fixed',
    bottom: '0',
    left: '0',
    right: '0',
    zIndex: '3000',
    padding: '10px 16px',
    background: '#fdf6ec',
    color: '#e6a23c',
    fontSize: '13px',
    textAlign: 'center',
    boxShadow: '0 1px 4px rgba(0,0,0,.15)'
  })

  const text = document.createElement('span')
  text.className = 'guard-error-tip__text'
  text.textContent = options.message || t('lang.initUserInfoFailedTips')
  box.appendChild(text)

  const btn = document.createElement('button')
  btn.className = 'guard-error-tip__retry'
  btn.type = 'button'
  btn.textContent = t('lang.retry')
  Object.assign(btn.style, {
    marginLeft: '12px',
    padding: '2px 12px',
    border: '1px solid #e6a23c',
    borderRadius: '3px',
    background: 'transparent',
    color: '#e6a23c',
    cursor: 'pointer'
  })
  btn.addEventListener('click', () => {
    // 重试即整页重载：初始化接口的结果散落在 store / localStorage 多处，重载最干净
    window.location.reload()
  })
  box.appendChild(btn)

  document.body.appendChild(box)
  return box
}

/** 移除错误提示区（鉴权恢复正常时调用） */
export function hideGuardErrorTip () {
  if (typeof document === 'undefined') return
  const exist = document.getElementById(GUARD_ERROR_TIP_ID)
  if (exist && exist.parentNode) exist.parentNode.removeChild(exist)
}
